import { useCallback, useEffect, useState, type KeyboardEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { createFeedback } from '../api';
import { errorMessage } from '../lib/api';
import { Modal } from './Modal';
import { Flash } from './Flash';
import { Spinner } from './Spinner';

/**
 * Lightweight in-app feedback capture: one blunt line of markdown plus the
 * route it was raised from. Feeds the Feedback Triage worker, not a support
 * inbox — no reply is expected.
 */
export function FeedbackModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const location = useLocation();
  const [body, setBody] = useState('');
  const [done, setDone] = useState(false);
  const trimmedLength = body.trim().length;
  const canSend = trimmedLength >= 3;

  const mutation = useMutation({
    mutationFn: () =>
      createFeedback({
        body: body.trim(),
        context: { path: location.pathname, label: document.title || location.pathname },
      }),
    onSuccess: () => {
      setBody('');
      setDone(true);
      onClose();
    },
  });

  // The modal is mounted permanently in Layout, so a stale error box or
  // success flag from a previous open must never carry into a fresh one.
  // Keep the typed body across cancel — only a successful send clears it.
  useEffect(() => {
    if (open) {
      mutation.reset();
      setDone(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleFlashDone = useCallback(() => setDone(false), []);

  function submit() {
    if (canSend && !mutation.isPending) mutation.mutate();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title="Send feedback to aiai-loop"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={!canSend || mutation.isPending}
              onClick={submit}
            >
              {mutation.isPending && <Spinner />}
              Send
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-xs text-ink-faint">
            About:{' '}
            <span className="font-mono text-ink-muted">{location.pathname}</span>
          </p>
          <textarea
            className="input h-28 resize-y"
            autoFocus
            aria-label="Your feedback"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="What felt rough? One blunt line is perfect."
          />
          {trimmedLength > 0 && trimmedLength < 3 && (
            <p className="text-xs text-ink-faint">
              {3 - trimmedLength} more character{3 - trimmedLength === 1 ? '' : 's'} needed
            </p>
          )}
          {mutation.isError && (
            <div className="rounded-[5px] border border-bad/30 bg-bad-soft px-3 py-2 text-sm text-bad">
              {errorMessage(mutation.error, 'Could not send feedback')}
            </div>
          )}
        </div>
      </Modal>
      {done && (
        <div className="fixed bottom-4 right-4 z-50 animate-fade-in rounded-[8px] border border-line-strong bg-surface-1 px-4 py-2.5 shadow-lg">
          <Flash onDone={handleFlashDone}>Thanks — this is in the queue.</Flash>
        </div>
      )}
    </>
  );
}
